import { ReactNode, useRef } from 'react';
import { ScalingType } from 'types/Scene';
import Icon, { IconTypes } from 'shared/Icon';
import { useClickAway, useToggle } from 'react-use';

import { SxStyleProp } from 'theme-ui';
import analytics from 'utils/analytics';
import { ToolName } from 'types/AppState';
import { useTranslator } from '@picsart/rc/services/localization';

type SelectOptionHandler = (scalingMode: ScalingType) => () => void;

const optionStyles: SxStyleProp = {
  display: 'flex',
  alignItems: 'center',
  color: 'background',
  bg: 'grey6',
  height: 24,
  fontSize: 1,
  fontWeight: 'bold',
  cursor: 'pointer',
  userSelect: 'none',
  px: 1,
  my: 1,
  borderRadius: 'xs',

  svg: {
    mr: 1
  }
};

const selectOptionStyles: SxStyleProp = {
  my: 0,
  width: '53px',
  height: '40px',
  justifyContent: 'center',
  color: 'text',
  bg: 'white',
  fontSize: '14px',
  fontWeight: 500,
  borderRadius: '8px',
  ':hover': {
    bg: 'grey1'
  }
};

const Option = ({
  children,
  selectable,
  selected,
  ...props
}: {
  children: (
    translator: (
      str: string,
      values?:
        | {
            [key: string]: any;
          }
        | undefined
    ) => string
  ) => ReactNode;
  onClick: () => void;
  selectable?: boolean;
  selected?: boolean;
  'data-test'?: string;
}) => {
  const styles = {
    ...optionStyles,
    ...(selectable ? selectOptionStyles : {}),
    ...(selected ? { bg: 'grey1' } : {})
  };
  const translator = useTranslator();
  return (
    <div sx={styles} {...props}>
      {children(translator)}
    </div>
  );
};

const FitOption = ({
  selectOptionHandler,
  selectable,
  selected
}: {
  selectOptionHandler: SelectOptionHandler;
  selectable?: boolean;
  selected?: boolean;
}) => (
  <Option
    selected={selected}
    selectable={selectable}
    onClick={selectOptionHandler(ScalingType.FIT)}
    data-test="scene_scaling-mode-dropdown_fit"
  >
    {translator => translator('Fit')}
  </Option>
);

const FillOption = ({
  selectOptionHandler,
  selectable,
  selected
}: {
  selectOptionHandler: SelectOptionHandler;
  selectable?: boolean;
  selected?: boolean;
}) => (
  <Option
    selected={selected}
    selectable={selectable}
    onClick={selectOptionHandler(ScalingType.FILL)}
    data-test="scene_scaling-mode-dropdown_fill"
  >
    {translator => translator('Fill')}
  </Option>
);

export const ScalingModeSelect = ({
  value,
  onChange
}: {
  onChange: (mode: ScalingType) => void;
  value: ScalingType;
}) => {
  const ref = useRef(null);
  const [opened, toggleOpened] = useToggle(false);
  useClickAway(ref, () => toggleOpened(false));

  const openOptionsHandler = () => {
    if (!opened) {
      toggleOpened(true);
      analytics.videoEditItemOpen(ToolName.SceneFit);
    }
  };
  const selectOptionHandler: SelectOptionHandler = scalingMode => () => {
    if (opened) {
      onChange(scalingMode);
      toggleOpened(false);
    }
  };

  return (
    <div sx={{ position: 'relative' }} ref={ref} data-test="scene_scaling-mode-dropdown">
      <div
        onClick={openOptionsHandler}
        sx={{ height: 24, alignItems: 'center', display: 'flex', cursor: 'pointer' }}
      >
        {value === ScalingType.FIT ? (
          <FitOption selectOptionHandler={selectOptionHandler} />
        ) : (
          <FillOption selectOptionHandler={selectOptionHandler} />
        )}
        <Icon
          sx={{ width: '9px', my: 1, fill: 'white' }}
          name="arrow-down-gen"
          type={IconTypes.White}
        />
      </div>
      {opened && (
        <div
          sx={{
            px: 2,
            py: 2,
            position: 'absolute',
            top: '100%',
            left: 0,
            borderRadius: '8px',
            bg: 'white',
            borderColor: 'grey3',
            borderWidth: '1px'
          }}
        >
          <FillOption
            selected={value === ScalingType.FILL}
            selectable={true}
            selectOptionHandler={selectOptionHandler}
          />
          <FitOption
            selected={value === ScalingType.FIT}
            selectable={true}
            selectOptionHandler={selectOptionHandler}
          />
        </div>
      )}
    </div>
  );
};
